import { useEffect, useState } from "react";
import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download } from "lucide-react";
import PageAnimator from '../../../components/ui/PageAnimator';
import api from "../../../api";
import { useAuth } from "../../../context/AuthProvider";
import ProspectProfile from "../../../features/prospection/components/ProspectProfile";
import ProspectInteractions from "../../../features/prospection/components/ProspectInteractions";
import { useNotification } from "../../../context/NotificationContext";
import ProspectStatus from "../../../features/prospection/components/ProspectStatus";
import ProspectsTable from "../../../features/prospection/components/ProspectsTable";
import ProspectForm from "../../../features/prospection/components/ProspectForm";
import ProspectHeader from "../../../features/prospection/components/ProspectHeader";
import ArchiveProspectsGrid from "../../../features/prospection/components/ArchiveGrid";
import { getPaginationState } from "../../../utils/pagination";

const EMPTY_PROSPECTS = [];

export default function Prospects() {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(5);
    const { user } = useAuth();
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [currentProspect, setCurrentProspect] = useState(null);
    const [showArchives, _setShowArchives] = useState(false);
    const [openArchive, setOpenArchive] = useState(false);
    const [archivedProspects, setArchivedProspects] = useState([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const { showToast } = useNotification();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const refreshProspects = () => queryClient.invalidateQueries({ queryKey: ["prospects"] });

    const { data, isLoading, isFetching, isPlaceholderData, error, refetch } = useQuery({
        queryKey: ["prospects", page, pageSize],
        queryFn: async () => {
            const res = await api.get(`/prospects?page=${page + 1}&limit=${pageSize}`);
            const result = res.data;
            if (!Array.isArray(result?.prospects) || !Number.isSafeInteger(result.total) || result.total < 0) {
                throw new Error("Réponse de pagination des prospects invalide.");
            }
            return { ...result, page, pageSize };
        },
        placeholderData: keepPreviousData,
    });

    const prospects = data?.prospects ?? EMPTY_PROSPECTS;
    const totalProspects = data?.total ?? 0;
    const displayedPage = data?.page ?? page;
    const displayedPageSize = data?.pageSize ?? pageSize;
    const { safePage } = getPaginationState({
        page: displayedPage + 1,
        pageSize: displayedPageSize,
        totalItems: totalProspects,
    });
    const pageOutOfBounds = Boolean(data) && displayedPage + 1 !== safePage;
    const loading = isLoading || pageOutOfBounds;
    const refreshing = isFetching || isPlaceholderData || pageOutOfBounds;

    useEffect(() => {
        if (data && !isPlaceholderData && pageOutOfBounds) {
            setPage(safePage - 1);
            setSelectedRows(new Set());
            setShowExportModal(false);
        }
    }, [data, isPlaceholderData, pageOutOfBounds, safePage]);

    // Une sélection appartient uniquement à la page reçue, même après une mutation.
    useEffect(() => {
        const availableIds = new Set(prospects.map(prospect => prospect._id || prospect.id));
        setSelectedRows(previous => {
            const remaining = new Set([...previous].filter(id => availableIds.has(id)));
            return remaining.size === previous.size ? previous : remaining;
        });
    }, [prospects]);

    const handlePageChange = nextPage => {
        setSelectedRows(new Set());
        setShowExportModal(false);
        setPage(nextPage);
    };

    const handlePageSizeChange = nextSize => {
        setSelectedRows(new Set());
        setShowExportModal(false);
        setPageSize(nextSize);
        setPage(0);
    };

    const deleteMutation = useMutation({
        mutationFn: async (id) => await api.delete(`/prospects/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["prospects"] });
            setSelectedProspect(null);
        },
        onError: (error) => console.error("Erreur lors de la suppression du prospect :", error)
    });

    const supprimerProspect = (id) => {
        deleteMutation.mutate(id);
    };

    const fetchArchivedProspects = async () => {
        try {
            const res = await api.get(`/prospects?archived=true&limit=1000`);
            setArchivedProspects(res.data.prospects || []);
        } catch (err) {
            console.error("Erreur lors de la récupération des prospects archivés :", err);
        }
    };

    const handleRestoreProspect = async (prospectId) => {
        try {
            await api.put(`/prospects/${prospectId}`, { archived: false });
            refreshProspects();
            fetchArchivedProspects();
            showToast("Prospect restauré avec succès", "success");
        } catch (error) {
            console.error("Erreur lors de la restauration :", error);
            showToast("Erreur lors de la restauration", "error");
        }
    };

    const handleDeleteProspect = async (prospectId) => {
        try {
            await api.delete(`/prospects/${prospectId}`);
            fetchArchivedProspects();
            showToast("Prospect supprimé définitivement", "success");
        } catch (error) {
            console.error("Erreur lors de la suppression :", error);
            showToast("Erreur lors de la suppression", "error");
        }
    };

    const handleArchiveProspect = async (prospectId) => {
        try {
            await api.put(`/prospects/${prospectId}`, { archived: true });
            refreshProspects();
            fetchArchivedProspects();
            showToast("Prospect archivé avec succès", "success");
        } catch (error) {
            console.error("Erreur lors de l'archivage :", error);
            showToast("Erreur lors de l'archivage", "error");
        }
    };

    const handleOpenForm = (prospect = null) => {
        setCurrentProspect(prospect);
        setOpenModal(true);
    };

    const handleViewDetails = (id) => {
        navigate(`/prospects/${id}`);
    };

    // ⚡ React Query : Ajout / Mise à jour (Mutation)
    const saveMutation = useMutation({
        mutationFn: async (newProspect) => {
            if (newProspect.id) { // Prisma utilise 'id' au lieu de '_id' en général, mais cela dépend de votre modèle.
                const res = await api.put(`/prospects/${newProspect.id}`, newProspect);
                return res.data;
            } else {
                const res = await api.post(`/prospects`, newProspect);
                return res.data;
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["prospects"] });
            setOpenModal(false);
            setSelectedProspect(data);
        },
        onError: (error) => console.error("Erreur de sauvegarde :", error)
    });

    const handleSaveProspect = (newProspect) => {
        saveMutation.mutate(newProspect);
    };

  const handleExportClick = () => {
    if (refreshing || error) return;
    if (selectedRows.size === 0) {
      showToast("Veuillez sélectionner au moins un prospect à exporter.", "error");
      return;
    }
    setShowExportModal(true);
  };

  const confirmExport = () => {
    if (refreshing || error) return;
    try {
      const dataToExport = prospects.filter(p => selectedRows.has(p._id || p.id));
      if (dataToExport.length === 0) {
          showToast("Aucune donnée à exporter.", "error");
          setShowExportModal(false);
          return;
      }

      const headers = ["Type", "Nom", "Prénom", "Email", "Téléphone", "Statut", "Profession", "Enveloppe estimée"];
      const csvContent = [
        headers.join(";"),
        ...dataToExport.map(p => [
          p.type || "",
          p.lastName || p.nom || "",
          p.firstName || p.prenom || "",
          p.email || "",
          p.phone || p.telephone || "",
          p.status || p.statut || "",
          p.position || p.profession || p.sector || p.secteurActivite || "",
          p.estimatedWealthBracket || ""
        ].map(v => `"${v}"`).join(";"))
      ].join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `prospects_export_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Clear selection after export
      setSelectedRows(new Set());
      setShowExportModal(false);
      showToast(`${dataToExport.length} fichier(s) exporté(s) avec succès !`, "success");
    } catch (err) {
      console.error("Export error", err);
      showToast("Une erreur est survenue lors de l'export.", "error");
      setShowExportModal(false);
    }
  };

    const handleStatutUpdate = (newStatut) => {
        if (!selectedProspect) return;
        setSelectedProspect((prev) => ({ ...prev, statut: newStatut }));
    };

    return (
        <div className="p-4">
            <PageAnimator>
            {/* 📋 Section Profil / Interactions / Statut */}
            {!showArchives && (
                <div className="flex gap-2 mb-4">
                    <ProspectProfile prospect={selectedProspect} onViewDetails={handleViewDetails} />
                    <ProspectInteractions prospect={selectedProspect} />
                    <ProspectStatus prospect={selectedProspect} onStatutUpdate={handleStatutUpdate} />
                </div>
            )}

            {/* Nouvel En-tête (entre les blocs et la table) */}
            <ProspectHeader 
                onAdd={() => handleOpenForm()}
                onArchiveView={() => {
                    setOpenArchive(true);
                    fetchArchivedProspects();
                }}
            />


            {/* 🗂️ Grille principale ou archive */}
            {showArchives ? (
                <ArchiveProspectsGrid 
                    prospects={archivedProspects} 
                    onRestore={handleRestoreProspect}
                    onDelete={handleDeleteProspect}
                    open={showArchives}
                    onClose={() => _setShowArchives(false)}
                />
            ) : (
                <ProspectsTable
                    prospects={prospects}
                    totalProspects={totalProspects}
                    loading={loading}
                    refreshing={refreshing}
                    error={error}
                    onRetry={refetch}
                    page={displayedPage}
                    pageSize={displayedPageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onDelete={supprimerProspect}
                    selectProspect={setSelectedProspect}
                    onEdit={handleOpenForm}
                    refreshProspects={refreshProspects}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    onExport={handleExportClick}
                    onViewDetails={handleViewDetails}
                    onArchive={handleArchiveProspect}
                />
            )}

            {/* 📝 Formulaire Prospect */}
            <ProspectForm
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSave={handleSaveProspect}
                prospect={currentProspect}
                referent={user?.Nom}
            />

            {openArchive && (
                <motion.div
                    className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="bg-surface rounded-lg shadow-xl w-[90vw] h-[80vh] p-6 relative"
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.9 }}
                    >
                        {/* ❌ Bouton de fermeture */}
                        <button
                            className="absolute top-3 right-3 text-subtle hover:text-primary"
                            onClick={() => setOpenArchive(false)}
                        >
                            ✕
                        </button>

                        {/* 📦 Grille d'archives des prospects */}
                        <div className="flex-1 overflow-auto rounded-lg border border-workspace mt-4">
                            <ArchiveProspectsGrid
                                prospects={archivedProspects}
                                onRestore={handleRestoreProspect}
                                onDelete={handleDeleteProspect}
                                open={openArchive}
                                onClose={() => setOpenArchive(false)}
                            />
                        </div>
                    </motion.div>
                </motion.div>
            )}

            {/* Modal de Confirmation d'Export */}
            <AnimatePresence>
                {showExportModal && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-sm flex justify-center items-center z-[80] p-4"
                    >
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="workspace-card-elevated p-6 rounded-xl w-full max-w-md"
                        >
                            <h3 className="text-xl font-bold mb-2 text-primary flex items-center gap-2">
                                <Download className="w-5 h-5 text-primary-500" />
                                Confirmer l'exportation
                            </h3>
                            <p className="text-sm text-subtle mb-6">
                                Vous êtes sur le point d'exporter <strong>{selectedRows.size}</strong> prospect(s) au format CSV. Voulez-vous continuer ?
                            </p>
                            <div className="flex justify-end gap-3 pt-4 border-t border-workspace">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowExportModal(false)}
                                >
                                    Annuler
                                </button>
                                <button
                                    className="btn btn-primary bg-primary-600 hover:bg-primary-700"
                                    onClick={confirmExport}
                                    disabled={refreshing || Boolean(error) || selectedRows.size === 0}
                                >
                                    <Download className="w-4 h-4 mr-2" /> Exporter
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            </PageAnimator>
        </div>
    );
}
